import React, { useEffect, useRef } from "react";
import { Doughnut } from "react-chartjs-2";

import { Text } from "@performyard/py-components";

import { useReviewCycleBuilderPageContext } from "../../hooks/ReviewCycleBuilderPageContext";

import { getColor } from "../../../../../utils/getColor";

import BaseReviewCycleDetailsCard from "../BaseReviewCycleDetailsCard";

type ReviewCompletionCardProps = {
	width: Parameters<typeof BaseReviewCycleDetailsCard>[0]["width"];
};

const ReviewCompletionCard = ({ width }: ReviewCompletionCardProps) => {
	const { reviewCycleReviewsDetails } = useReviewCycleBuilderPageContext();

	const colors = {
		completed: getColor("green", 5),
		closed: getColor("red", 5),
		active: getColor("primary", 5),
	};

	// Track review count for canvas renders
	const reviewCountRef = useRef(0);
	// Update review count when reviewCycleReviewsDetails changes
	useEffect(() => {
		reviewCountRef.current = reviewCycleReviewsDetails?.length || 0;
	}, [reviewCycleReviewsDetails]);

	const reviewCount = reviewCycleReviewsDetails?.length || 0;
	const completedCount =
		reviewCycleReviewsDetails?.filter((review) => review.completed && !review.closed).length || 0;
	const closedCount = reviewCycleReviewsDetails?.filter((review) => review.closed).length || 0;
	const activeCount = reviewCycleReviewsDetails?.filter((review) => !review.completed && !review.closed).length || 0;

	const chartData = [
		{ label: "Completed", count: completedCount, color: colors.completed },
		{ label: "Closed", count: closedCount, color: colors.closed },
		{ label: "Active", count: activeCount, color: colors.active },
	];

	const data = {
		labels: ["Completed", "Closed", "Active"],
		datasets: [
			{
				data: [completedCount, closedCount, activeCount],
				backgroundColor: [colors.completed, colors.closed, colors.active],
				borderColor: [colors.completed, colors.closed, colors.active],
				borderWidth: 0,
			},
		],
	};

	const doughnutLabel = {
		id: "doughnut-label",
		afterDatasetsDraw: (chart) => {
			const { ctx } = chart;

			const centerX = chart.getDatasetMeta(0).data[0].x;
			const centerY = chart.getDatasetMeta(0).data[0].y;

			ctx.save();
			// draw top label with
			// neutral 9 color, 24.138px size and 600 font weight and 34.483 line height
			ctx.fillStyle = "#1A1A1A";
			ctx.font = "400 24.138px Source Sans Pro, sans-serif";
			ctx.textAlign = "center";
			ctx.textBaseline = "middle";
			ctx.fillText(`${reviewCountRef.current}`, centerX, centerY - 2); // Use ref to re-render the chart when review count changes
			// draw bottom label with neutral 9 color, 14px size and 400 font weight and 17.2415 line height
			ctx.font = "400 12px Source Sans Pro, sans-serif";
			ctx.fillText(`Reviews`, centerX, centerY + 14.2415);

			ctx.restore();
		},
	};

	return (
		<BaseReviewCycleDetailsCard width={width}>
			<div className="flex h-full flex-col gap-4">
				<Text size="paragraph-3" weight="semi-bold" color="neutral-9">
					Review Completion
				</Text>
				<div className="flex shrink grow justify-start overflow-hidden">
					<div className="w-[100px] pr-2">
						<Doughnut
							data={data}
							plugins={[doughnutLabel]}
							options={{
								cutout: "75%",
								layout: {
									padding: 0,
								},
								maintainAspectRatio: false,
								responsive: true,
								plugins: {
									legend: {
										display: false,
									},
								},
							}}
						/>
					</div>
					<div className="flex flex-col justify-center gap-2 pl-2">
						{chartData.map((item, index) => (
							<div key={index} className="flex items-center gap-2">
								<div className="h-3 w-3 rounded-full" style={{ backgroundColor: item.color }} />
								<div className="flex items-center gap-2">
									<Text size="paragraph-3" color="neutral-9">
										{`${item.count} ${item.label} (${reviewCount ? ((item.count / reviewCount) * 100).toFixed(0) : 0}%)`}
									</Text>
								</div>
							</div>
						))}
					</div>
				</div>
			</div>
		</BaseReviewCycleDetailsCard>
	);
};

export default ReviewCompletionCard;
